import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { forwardRef, useContext, useEffect, useRef, useState } from 'react';
import CustomSelect2 from './CustomSelect2';
import { Box, Typography, FormControlLabel, Grid, Checkbox, TextField, InputAdornment, Select, MenuItem, Skeleton, List, ListItem, ListItemText, colors, Button, Chip } from '@mui/material';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import { AuthContext } from '../utils/AuthContext';
import { getRates } from '../services/QuotationService';
import SelectPlanCheckbox from './SelectPlanCheckbox';
import { ConfigContext } from '../utils/ConfigContext';
import TableRatesRow from './TableRatesRow';
import getSafe from '../utils/getSafe';
import domtoimage from 'dom-to-image';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import NotesDisplay from './NotesDisplay';
import RatesVersionSelect from './RatesVersionSelect';
import CopyImageButton from './CopyImageButton';
import CopyExcelDataButton from './CopyExcelDataButton';



const MyHealthPhilippines_Rates = forwardRef(({ appConfig, sx, my_health_philippines, clients, currency, _currency, selectedPlans,
  setSelectedPlans, loadingStatus, response, deductibles, setDeductibles, addToPendingTasks, removeFromPendingTasks,
  discounts, setDiscounts, computedRates, setComputedRates,
  myHealth_HS_Plan, setMyHealth_HS_Plan, myHealth_HS_Deductible,
  setMyHealth_HS_Deductible, myHealth_OP_Plan, setMyHealth_OP_Plan,
  myHealth_OP_CoIns, setMyHealth_OP_CoIns, myHealth_MA_Plan,
  setMyHealth_MA_Plan, myHealth_DN_Plan, setMyHealth_DN_Plan,
  getCoInsLabel, areaOfCoverage, countryOfResidence,
  specificAreaOfCoverage, setSpecificAreaOfCoverage,
  setShowImageCopy, selectedOptions, addSelectedOption,
  setViewSelectedOption, specificCurrency, setSpecificCurrency,
  specificPaymentFrequency, setSpecificPaymentFrequency , insurerRates = [], selectedRatesVersion, setSelectedRatesVersion, handleFetchAppConfig, setShowRefreshNow }, ref) => {

  const id = 'my_health_philippines';

  // const { appConfig } = useContext(ConfigContext);

  const containerRef = useRef(null);

  const { auth } = useContext(AuthContext);

  // Each comparison is another table of plan selections (one per client) priced against the
  // same options as the main table: { id, selections: [{ hs_plan, deductible, op_plan, co_ins, ma_plan, dn_plan }] }.
  // Their rates are fetched separately from the main request, keyed by comparison id.
  const [comparisons, setComparisons] = useState([]);
  const [comparisonRates, setComparisonRates] = useState({});
  const comparisonIdCounter = useRef(0);
  const comparisonRequestIds = useRef({});

  const plans =getSafe(() => appConfig.content.specifics.my_health_philippines.plans);
  const hs_plans = getSafe(() => appConfig.content.specifics.my_health_philippines.hs_plans);
  const area_of_cover = getSafe(() => appConfig.content.specifics.my_health_philippines.area_of_cover);
  const family_discount = getSafe(() => appConfig.content.specifics.my_health_philippines.family_discount);

  const premium_tax_rate = getSafe(() => appConfig.content.specifics.my_health_philippines.premium_tax, 0);
  const local_government_tax_rate = getSafe(() => appConfig.content.specifics.my_health_philippines.local_government_tax, 0);
  const documentary_stamp_tax_usd = getSafe(() => appConfig.content.specifics.my_health_philippines.documentary_stamp_tax_usd, 0);


  const specific_area_of_coverages = getSafe(() => appConfig.content.my_health_philippines_area_of_coverage);

  const currencies = getSafe(() => appConfig.content.currencies, []);


  const [validationErrorMessages, setValidationErrorMessages] = useState([]);

  useEffect(() => {
    const errors = validateRequirements();

    // if (errors.length > 0) {
    //   removeFromPendingTasks([id]);
    // }
  }, [countryOfResidence, myHealth_MA_Plan, myHealth_HS_Plan, myHealth_HS_Deductible, myHealth_OP_Plan, clients, getAreaOfCoverage()]);

  useEffect(() => {
    const idx = selectedPlans.indexOf(id);

    if (my_health_philippines && checkIfValidForPremiumSummary()) {
      if (idx == -1) {
        selectedPlans.push(id);
      }
    } else {
      if (idx != -1) {
        selectedPlans.splice(idx, 1);
      }
    }

    setSelectedPlans([...selectedPlans]);
  }, [my_health_philippines, checkIfValidForPremiumSummary()]);

  useEffect(() => {
    computedRates[id] = getComputedRates();
    setComputedRates(computedRates);


  }, [response, discounts, comparisons, comparisonRates]);

  // The main rates were refreshed (clients, area of coverage, rates version...), refresh the comparisons too
  useEffect(() => {
    comparisons.forEach(fetchComparisonRates);
  }, [response]);

  // Newly added or restored comparisons
  useEffect(() => {
    comparisons.filter((comparison) => !comparisonRates[comparison.id]).forEach(fetchComparisonRates);
  }, [comparisons]);

  useEffect(() => {
    if (myHealth_HS_Deductible.length == 0) return;

    deductibles[id] = myHealth_HS_Deductible[0];
  }, [myHealth_HS_Deductible]);

  useEffect(() => {
    setTimeout(() => {
      addSelectedOption(id, 'specific_area_of_coverage', specificAreaOfCoverage[id]);
      addSelectedOption(id, 'specific_currency', specificCurrency[id]);
      addSelectedOption(id, 'specific_payment_frequency', specificPaymentFrequency[id]);
      addSelectedOption(id, 'comparisons', JSON.stringify(comparisons));
    }, 1000);
  }, [specificAreaOfCoverage[id], specificCurrency[id], specificPaymentFrequency[id], comparisons]);

  useEffect(() => {
    setViewSelectedOption(id, 'specific_area_of_coverage', (option_value) => {
      specificAreaOfCoverage[id] = option_value;
      setSpecificAreaOfCoverage(specificAreaOfCoverage);
    });

    setViewSelectedOption(id, 'specific_currency', (option_value) => {
      specificCurrency[id] = option_value;
      setSpecificCurrency(specificCurrency);
    });

    setViewSelectedOption(id, 'specific_payment_frequency', (option_value) => {
      specificPaymentFrequency[id] = option_value;
      setSpecificPaymentFrequency(specificPaymentFrequency);
    });

    setViewSelectedOption(id, 'comparisons', (option_value) => {
      let parsed = option_value;
      if (typeof parsed === 'string') {
        try {
          parsed = JSON.parse(parsed);
        } catch (e) {
          parsed = [];
        }
      }
      parsed = Array.isArray(parsed) ? parsed.filter((comparison) => comparison && Array.isArray(comparison.selections)) : [];

      comparisonRequestIds.current = {};
      setComparisonRates({});
      setComparisons(parsed);

      const maxId = parsed.reduce((max, comparison) => Math.max(max, comparison.id || 0), 0);
      comparisonIdCounter.current = Math.max(comparisonIdCounter.current, maxId);
    });
  }, [selectedOptions]);


  function getComputedRates() {
    if (!response) return;

    const rates = response.rates;
    const discount = discounts[id];
    const totalPercent = (100 - discount) * 0.01;


    const computedRates = [];

    for (let i = 0; i < rates.length; i++) {
      computedRates.push({
        'client': rates[i].client,
        'my_health_philippines': getSubTotalRate(i),
        'currency': rates[i].currency,
        'plan_label': getPlanLabel(),
        // Shown as extra columns in the Premiums Summary
        'comparisons': comparisons.map((comparison) => ({
          id: comparison.id,
          plan_label: getPlanLabel((clientIdx) => getComparisonSelection(comparison, clientIdx)),
          deductible: getComparisonSelection(comparison, 0).deductible,
          [id]: comparisonRates[comparison.id]?.rates ? getSubTotalRate(i, comparisonRates[comparison.id].rates) : '',
        }))
      })
    }

    return computedRates;
  }

  function getPlanLabel(getSelection = getMainSelection) {
    if (clients.length === 0) return '';

    const selection = getSelection(0);

    let label = '';

    label = 'HS ' + selection.hs_plan;

    if (selection.op_plan !== 'N/A') {
      label += ' + ' + 'OP ' + selection.op_plan;
    }

    if (selection.ma_plan !== 'N/A') {
      label += ' + ' + 'MA ' + selection.ma_plan;
    }

    if (selection.dn_plan !== 'N/A') {
      label += ' + ' + 'DN ' + selection.dn_plan;
    }

    return label;
  }

  function getMainSelectionState() {
    return {
      hs_plan: [myHealth_HS_Plan, setMyHealth_HS_Plan],
      deductible: [myHealth_HS_Deductible, setMyHealth_HS_Deductible],
      op_plan: [myHealth_OP_Plan, setMyHealth_OP_Plan],
      co_ins: [myHealth_OP_CoIns, setMyHealth_OP_CoIns],
      ma_plan: [myHealth_MA_Plan, setMyHealth_MA_Plan],
      dn_plan: [myHealth_DN_Plan, setMyHealth_DN_Plan],
    };
  }

  function getMainSelection(clientIdx) {
    const selection = {};

    Object.entries(getMainSelectionState()).forEach(([field, [values]]) => {
      selection[field] = values[clientIdx];
    });

    return selection;
  }

  function handleMainSelectionChange(clientIdx, field, value) {
    const [values, setValues] = getMainSelectionState()[field];

    values[clientIdx] = value;
    setValues([...values]);

    addToPendingTasks([id]);
  }

  // Clients added after the comparison was made start with the main table's selection
  function getComparisonSelection(comparison, clientIdx) {
    return comparison.selections[clientIdx] || getMainSelection(clientIdx);
  }

  function nextComparisonId() {
    comparisonIdCounter.current += 1;
    return comparisonIdCounter.current;
  }

  // A new comparison starts as a copy of the main table, for the user to change its plans
  function addComparison() {
    const comparison = {
      id: nextComparisonId(),
      selections: clients.map((client, clientIdx) => getMainSelection(clientIdx)),
    };

    setComparisons([...comparisons, comparison]);
  }

  function removeComparison(comparisonId) {
    delete comparisonRequestIds.current[comparisonId];

    setComparisons(comparisons.filter((comparison) => comparison.id !== comparisonId));
    setComparisonRates((prev) => {
      const next = { ...prev };
      delete next[comparisonId];
      return next;
    });
  }

  function clearComparisons() {
    comparisonRequestIds.current = {};

    setComparisons([]);
    setComparisonRates({});
  }

  function handleComparisonSelectionChange(comparisonId, clientIdx, field, value) {
    const comparison = comparisons.find((item) => item.id === comparisonId);

    if (!comparison) return;

    const selections = clients.map((client, idx) => ({ ...getComparisonSelection(comparison, idx) }));
    selections[clientIdx][field] = value;

    const updated = { ...comparison, selections };

    setComparisons(comparisons.map((item) => item.id === comparisonId ? updated : item));

    // Like the main table, wait for Refresh; the refreshed response then refetches every comparison
    addToPendingTasks([id]);
  }

  async function fetchComparisonRates(comparison) {
    if (clients.length === 0) return;

    const requestId = (comparisonRequestIds.current[comparison.id] || 0) + 1;
    comparisonRequestIds.current[comparison.id] = requestId;

    setComparisonRates((prev) => ({ ...prev, [comparison.id]: { ...prev[comparison.id], loading: true } }));

    const selections = clients.map((client, clientIdx) => getComparisonSelection(comparison, clientIdx));

    const data = await getRates({
      token: auth.token,
      body: {
        clients,
        deductibles: { [id]: selections[0].deductible },
        insurers: [id],
        specifics: {
          [id]: selections.map((selection) => ({
            hs: {
              plan: selection.hs_plan,
              deductible: selection.deductible
            },
            op: {
              plan: selection.op_plan,
              co_ins: selection.co_ins
            },
            ma: {
              plan: selection.ma_plan
            },
            dn: {
              plan: selection.dn_plan
            }
          }))
        },
        specific_area_of_coverage: { [id]: specificAreaOfCoverage[id] },
        rates_version_per_insurer: { [id]: selectedRatesVersion?.[id] || '' },
        specific_currency: { [id]: specificCurrency[id] },
        specific_payment_frequency: { [id]: specificPaymentFrequency[id] },
      }
    }).then((res) => res?.data?.[id]).catch(() => null);

    // Removed, or superseded by a newer request
    if (comparisonRequestIds.current[comparison.id] !== requestId) return;

    setComparisonRates((prev) => ({ ...prev, [comparison.id]: { loading: false, rates: data?.rates || null } }));
  }

  function checkIfValidForPremiumSummary() {
    if (clients.length == 0) return false;
    // if (clients.length == 1) return true;

    // let hs = myHealth_HS_Plan[0], op = myHealth_OP_Plan[0], ma = myHealth_MA_Plan[0], dn = myHealth_DN_Plan[0],
    //   deductible = myHealth_HS_Deductible[0];

    // for (let i = 1; i < clients.length; i++) {
    //   if (hs != myHealth_HS_Plan[i]) return false;

    //   if (deductible != myHealth_HS_Deductible[i]) return false;

    //   if (op != myHealth_OP_Plan[i]) return false;

    //   if (ma != myHealth_MA_Plan[i]) return false;

    //   if (dn != myHealth_DN_Plan[i]) return false;

    // }


    return true;
  }

  function handleDeductibleChange(e) {
    const _deductibles = JSON.parse(JSON.stringify(deductibles));

    _deductibles.lifeline = e.target.value;

    setDeductibles(_deductibles);

    addToPendingTasks([id]);
  }

  function scrollToView() {
    setTimeout(() => {
      containerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 200);
  }

  function onInputDiscountChange(e) {
    var numericValue = e.target.value.replace(/[^0-9]/g, '');

    // Remove leading zeros
    numericValue = numericValue.replace(/^0+/, '');

    if (numericValue > 100) {
      numericValue = 100;
    }

    discounts[id] = numericValue;

    setDiscounts({ ...discounts });
  }

  function getRate(clientIndex, benefit, rates = response?.rates) {
    if (!rates) return '';

    if (!rates[clientIndex]) return '';

    return rates[clientIndex][benefit];
  }

  function getSubTotalRate(clientIndex, rates = response?.rates) {
    if (!rates) return '';

    if (!rates[clientIndex]) return 0;

    let hs_rates = getRate(clientIndex, 'hs', rates);
    let op_rates = getRate(clientIndex, 'op', rates);
    let ma_rates = getRate(clientIndex, 'ma', rates);
    let dn_rates = getRate(clientIndex, 'dn', rates);

    if (hs_rates === '') {
      hs_rates = 0;
    }

    if (op_rates === '') {
      op_rates = 0;
    }

    if (ma_rates === '') {
      ma_rates = 0;
    }

    if (dn_rates === '') {
      dn_rates = 0;
    }



    return hs_rates + op_rates + ma_rates + dn_rates;
  }


  function getTotalPremium(rates = response?.rates) {
    let totalPremium = 0;

    for (let i = 0; i < clients.length; i++) {
      totalPremium += getSubTotalRate(i, rates);
    }

    return totalPremium;
  }

  function computeTotalTax(rates = response?.rates) {
    let totalTax = 0;

    const totalPremium = getTotalPremium(rates);

    totalTax += totalPremium * premium_tax_rate;
    totalTax += totalPremium * local_government_tax_rate;
    totalTax += documentary_stamp_tax_usd

    return totalTax;
  }

  function computeTotalPremiumWithTax(rates = response?.rates) {
    return getTotalPremium(rates) + computeTotalTax(rates);
  }

  function formatRate(rate) {
    if (typeof rate === 'string' && rate === '') {
      return '----';
    }

    if (isNaN(rate) || rate == 'Unavailable' || typeof rate === 'string') {
      return <span style={{ color: '#f44336' }}>{'Unavailable'}</span>
    }

    if (isNaN(rate)) {
      return rate;
    }

    return getCurrencySign() + rate.toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  function getCurrencySign() {
    return '$';
  }

  function getFamilyDiscountLabel() {
    const clients_count = clients.length;

    if (!family_discount) {
      return '0%';
    }

    let discount = 0;

    if (clients_count > 7) {
      discount = family_discount['>7'];
    } else {
      discount = family_discount[clients_count] || 0;
    }

    return `${discount * 100}%`;
  }

  function getAreaOfCoverage() {

    let generalAreaOfCoverage = areaOfCoverage;

    if (specificAreaOfCoverage[id] === 'parent') {
      generalAreaOfCoverage = areaOfCoverage;
    } else {
      generalAreaOfCoverage = specificAreaOfCoverage[id];
    }

    const specificArea = area_of_cover[generalAreaOfCoverage] || '';

    return specificArea;
  }

  function getSpecificAreaOfCoverage(clientIndex) {

    if (clients.length == 0) return '';

    if (!clients[clientIndex]) return '';

    const specificArea = area_of_cover[clients[clientIndex].area_of_coverage] || '';

    return specificArea;
  }

  function validateRequirements() {
    const errors = [];

    // if (!validateCountryOfResidence()) {
    //   errors.push('Invalid Country of Residence. Only Available in Indonesia');
    // }

    const maternityErrors = validateMaternityQualification();

    if (maternityErrors.length > 0) {
      errors.push(...maternityErrors);
    }

    setValidationErrorMessages(errors);

    return errors;
  }


  function validateMaternityQualification(getSelection = getMainSelection) {
    const validations = {
      validation_1: {
        error: false,
        message: 'Maternity is only available if Nil deductible, Elite or Extensive Hospital and Surgery, and an Outpatient plan have been selected. Please change your selection.'
      },
      validation_2: {
        error: false,
        message: "Maternity is only available to Females aged from 19 to 45. Please adjust your selection and remove maternity from ineligible members' selection"
      }
    }

    for (let i = 0; i < clients.length; i++) {
      const selection = getSelection(i);

      if (selection.ma_plan && selection.ma_plan !== 'N/A') {
        // validation 1
        if (selection.deductible !== 'Nil') {
          validations['validation_1']['error'] = true;
        }

        if (!(['Elite', 'Extensive'].includes(selection.hs_plan))) {
          validations['validation_1']['error'] = true;
        }

        if (selection.op_plan === 'N/A') {
          validations['validation_1']['error'] = true;
        }

        // validation 2
        if (clients[i].gender !== 'Female') {
          validations['validation_2']['error'] = true;
        }

        if (clients[i].age < 19 || clients[i].age > 45) {
          validations['validation_2']['error'] = true;
        }
      }
    }

    const errorMessages = [];

    if (validations['validation_1']['error']) {
      errorMessages.push(validations['validation_1']['message'])
    } 
    
    if (validations['validation_2']['error']) {
      errorMessages.push(validations['validation_2']['message'])
    }


    return errorMessages;
  }

  function getMA_Plans() {
    const ma_plans = [...plans];

    const index = ma_plans.indexOf('Core');

    if (index > -1) {
      ma_plans.splice(index, 1);
    }

    return ma_plans;

  }

  // Used by the main table and each comparison table, which only differ in their plan selections and rates
  function renderRatesTable({ title, actions, tableId, getSelection, onSelectionChange, rates, loading }) {
    return (
      <>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'end', width: '100%', marginBottom: 7, marginTop: title ? 16 : 0 }}>
          <Typography fontSize={18} fontWeight={600} color='#004161' sx={{ mr: 1.5 }}>{title}</Typography>
          {actions}
          <div style={{ flex: 1 }}></div>
          <CopyExcelDataButton
            elementId={tableId}
            style={{ marginRight: 7, fontSize: 11, padding: '2px 8px', minHeight: 0 }}
            sx={{ '& .MuiSvgIcon-root': { fontSize: 14 } }}
          />
          <CopyImageButton
            elementId={tableId}
            setShowImageCopy={setShowImageCopy}
            style={{ fontSize: 11, padding: '2px 8px', minHeight: 0 }}
            sx={{ '& .MuiSvgIcon-root': { fontSize: 14 } }}
          />
        </div>

        <TableContainer component={Paper}>
          <Table id={tableId} size='small' sx={{
            background: '#fff',
            '& .MuiTableCell-root': { fontSize: 12 }
          }}>
            <TableHead>
              <TableRow style={{ background: '#004161' }}>
                <TableCell style={{ fontWeight: 600, color: '#fff', width: 200 }}>Clients</TableCell>
                <TableCell colSpan={3} style={{ fontWeight: 600, color: '#fff', borderLeft: 1, borderColor: '#a3a3a3' }}>Hospital & Surgery</TableCell>
                <TableCell colSpan={3} style={{ fontWeight: 600, color: '#fff', borderLeft: 1, borderColor: '#a3a3a3' }}>Outpatient</TableCell>
                <TableCell colSpan={2} style={{ fontWeight: 600, color: '#fff', borderLeft: 1, borderColor: '#a3a3a3' }}>Maternity</TableCell>
                <TableCell colSpan={2} style={{ fontWeight: 600, color: '#fff', borderLeft: 1, borderColor: '#a3a3a3' }}>Dental</TableCell>
                <TableCell style={{ fontWeight: 600, color: '#fff', borderLeft: 1, borderColor: '#a3a3a3' }}>Subtotal</TableCell>
              </TableRow>


            </TableHead>
            <TableBody>
              <TableRow style={{ background: '#C9D213' }}>
                <TableCell></TableCell>
                <TableCell style={{ color: '#004161', fontWeight: 600, borderLeft: 1, borderColor: '#a3a3a3' }}>Plan & Room</TableCell>
                <TableCell style={{ color: '#004161', fontWeight: 600 }}>Deductible</TableCell>
                <TableCell style={{ color: '#004161', fontWeight: 600 }}>Premium</TableCell>
                <TableCell style={{ color: '#004161', fontWeight: 600, borderLeft: 1, borderColor: '#a3a3a3' }}>Plan</TableCell>
                <TableCell style={{ color: '#004161', fontWeight: 600 }}>Co-ins.</TableCell>
                <TableCell style={{ color: '#004161', fontWeight: 600 }}>Premium</TableCell>
                <TableCell style={{ color: '#004161', fontWeight: 600, borderLeft: 1, borderColor: '#a3a3a3' }}>Plan</TableCell>
                <TableCell style={{ color: '#004161', fontWeight: 600 }}>Premium</TableCell>
                <TableCell style={{ color: '#004161', fontWeight: 600, borderLeft: 1, borderColor: '#a3a3a3' }}>Plan</TableCell>
                <TableCell style={{ color: '#004161', fontWeight: 600 }}>Premium</TableCell>
                <TableCell style={{ borderLeft: 1, borderColor: '#a3a3a3' }}></TableCell>
              </TableRow>
              {
                clients.map((client, client_idx) => {
                  const selection = getSelection(client_idx);

                  return (
                    <TableRow key={client.uid}>
                      {
                        client.full_name.trim() === '' ?
                          <TableCell style={{ fontWeight: 600 }} key={client.id}>_</TableCell>
                          :
                          <TableCell style={{ fontWeight: 600 }} key={client.id}>
                            <span>{client.full_name}
                              <span> ({client.gender == 'Male' ? 'M' : 'F'}) ({client.age}) </span></span>
                            <span>{client.country_of_residence}</span>
                          </TableCell>
                      }
                      <TableCell style={{ borderLeft: '1px solid #a3a3a3' }}>
                        <CustomSelect2 dense items={[...hs_plans]}
                          value={selection.hs_plan}
                          onChange={(e) => onSelectionChange(client_idx, 'hs_plan', e.target.value)}
                          style={{ width: '100%' }}
                        />
                      </TableCell>
                      <TableCell>
                        <CustomSelect2 dense items={getSafe(() => appConfig.content.deductibles.my_health_philippines['USD'])}
                          value={selection.deductible}
                          onChange={(e) => onSelectionChange(client_idx, 'deductible', e.target.value)}
                          style={{ width: '100%' }} />
                      </TableCell>
                      {
                        loading ?
                          <TableCell width={70}><Skeleton variant="rounded" width='100%' height={20} /></TableCell>
                          :
                          <TableCell width={70}>{formatRate(getRate(client_idx, 'hs', rates))}</TableCell>
                      }

                      <TableCell style={{ borderLeft: '1px solid #a3a3a3' }}>
                        <CustomSelect2 dense items={['N/A', ...plans]}
                          value={selection.op_plan}
                          onChange={(e) => onSelectionChange(client_idx, 'op_plan', e.target.value)}
                          style={{ width: '100%' }} />
                      </TableCell>
                      <TableCell>
                        <CustomSelect2 dense items={getCoInsLabel()}
                          value={selection.co_ins}
                          onChange={(e) => onSelectionChange(client_idx, 'co_ins', e.target.value)}
                          style={{ width: '100%' }}
                          isZeroDeductibleUniform={false}
                        />
                      </TableCell>
                      {
                        loading ?
                          <TableCell width={70}><Skeleton variant="rounded" width='100%' height={20} /></TableCell>
                          :
                          <TableCell width={70}>{formatRate(getRate(client_idx, 'op', rates))}</TableCell>
                      }
                      <TableCell style={{ borderLeft: '1px solid #a3a3a3' }}>
                        <CustomSelect2 dense items={['N/A', ...getMA_Plans()]}
                          value={selection.ma_plan}
                          onChange={(e) => onSelectionChange(client_idx, 'ma_plan', e.target.value)}
                          style={{ width: '100%' }} />
                      </TableCell>
                      {
                        loading ?
                          <TableCell width={70}><Skeleton variant="rounded" width='100%' height={20} /></TableCell>
                          :
                          <TableCell width={70}>{formatRate(getRate(client_idx, 'ma', rates))}</TableCell>
                      }
                      <TableCell style={{ borderLeft: '1px solid #a3a3a3' }}>
                        <CustomSelect2 dense items={['N/A', ...plans]}
                          value={selection.dn_plan}
                          onChange={(e) => onSelectionChange(client_idx, 'dn_plan', e.target.value)}
                          style={{ width: '100%' }} />
                      </TableCell>
                      {
                        loading ?
                          <TableCell width={70}><Skeleton variant="rounded" width={70} height={20} /></TableCell>
                          :
                          <TableCell width={70}>{formatRate(getRate(client_idx, 'dn', rates))}</TableCell>
                      }
                      {
                        loading ?
                          <TableCell style={{ borderLeft: '1px solid #a3a3a3' }} width={70}><Skeleton variant="rounded" width='100%' height={20} /></TableCell>
                          :
                          <TableCell style={{ borderLeft: '1px solid #a3a3a3' }}>{formatRate(getSubTotalRate(client_idx, rates))}</TableCell>
                      }

                    </TableRow>
                  )
                })
              }
              <TableRow>
                <TableCell colSpan={9} sx={{ py: 2.7 }}></TableCell>
                <TableCell colSpan={2} style={{ fontWeight: 600, textAlign: 'right' }}>Annual Premium {specificCurrency['my_health_philippines']}</TableCell>
                <TableCell style={{ fontWeight: 600, background: '#C9D213', color: '#004161', borderLeft: '1px solid #a3a3a3' }}>
                  {
                    loading ?
                      <Skeleton variant="rounded" width='100%' height={20} />
                      :
                      formatRate(getTotalPremium(rates))
                  }
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell colSpan={9} sx={{ py: 2.7 }}></TableCell>
                <TableCell colSpan={2} style={{ fontWeight: 600, textAlign: 'right' }}>Tax {specificCurrency['my_health_philippines']}</TableCell>
                <TableCell style={{ fontWeight: 600, background: '#C9D213', color: '#004161', borderLeft: '1px solid #a3a3a3' }}>
                  {
                    loading ?
                      <Skeleton variant="rounded" width='100%' height={20} />
                      :
                      formatRate(computeTotalTax(rates))
                  }
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell colSpan={9} sx={{ py: 2.7 }}></TableCell>
                <TableCell colSpan={2} style={{ fontWeight: 600, textAlign: 'right' }}>Annual Premium including Tax {specificCurrency['my_health_philippines']}</TableCell>
                <TableCell style={{ fontWeight: 600, background: '#C9D213', color: '#004161', borderLeft: '1px solid #a3a3a3' }}>
                  {
                    loading ?
                      <Skeleton variant="rounded" width='100%' height={20} />
                      :
                      formatRate(computeTotalPremiumWithTax(rates))
                  }
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>

        </TableContainer>
      </>
    );
  }

  return (
    <Box id={id} sx={sx}>
      <Paper sx={{ borderColor: '#e0e0e0', borderWidth: 2, pb: 2, scrollMarginTop: '200px' }} variant='outlined' ref={containerRef}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#004161', px: 2, py: 1 }}>
          <div style={{
            height: 60,
            padding: 6,
            fontFamily: 'Poppins, sans-serif',
            fontSize: 18,
            lineHeight: 1.2,
            fontWeight: 600
          }}><span style={{ color: '#fff' }}>MyHEALTH</span> <span style={{ color: '#6a9a4a' }}>Philippines</span></div>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <img src={`${process.env.PUBLIC_URL}/images/Paramount Logo.png`} alt="Paramount" style={{ height: 50, userSelect: 'none', WebkitUserDrag: 'none' }} draggable={false} onContextMenu={(e) => e.preventDefault()} />
            <img src={`${process.env.PUBLIC_URL}/images/April International Logo.png`} alt="April International" style={{ height: 50, userSelect: 'none', WebkitUserDrag: 'none' }} draggable={false} onContextMenu={(e) => e.preventDefault()} />
          </Box>
        </Box>
        <Box sx={{ p: 2 }}>

          <Grid container alignItems='center'>
            <Grid item xs={2}><Typography fontSize={13} fontWeight={600}>Select Area Of Cover</Typography></Grid>
            <Grid item xs={2}>
              <Select
                size='small'
                value={specificAreaOfCoverage[id]}
                onChange={(e) => {
                  specificAreaOfCoverage[id] = e.target.value;
                  setSpecificAreaOfCoverage({ ...specificAreaOfCoverage });

                  addToPendingTasks([id]);
                }}
                sx={{ fontSize: 12, height: 32, width: '170px' }}
                fullWidth
                disabled={loadingStatus[id]}
              >
                {
                  specific_area_of_coverages && specific_area_of_coverages.map(area_of_coverage => (
                    <MenuItem key={area_of_coverage} value={area_of_coverage} sx={{ fontSize: 12 }}>{area_of_coverage}</MenuItem>
                  ))
                }

              </Select>
            </Grid>
          </Grid>
          <Grid container alignItems='center' sx={{ mt: 1.5 }}>
            <Grid item xs={2}><Typography fontSize={13} fontWeight={600}>Currency</Typography></Grid>
            <Grid item xs={2}>
              <Select
                size='small'
                value={specificCurrency[id]}
                onChange={(e) => {
                  specificCurrency[id] = e.target.value;
                  setSpecificCurrency({ ...specificCurrency });

                  setDeductibles(prev => {
                    return {
                      ...prev,
                      [id]: 'Nil Deductible'
                    };
                  });


                  addToPendingTasks([id]);
                }}
                sx={{ fontSize: 12, height: 32, width: '170px', minWidth: 170 }}
                fullWidth
                disabled={loadingStatus[id]}
              >
                {
                  ["USD"].map(currency => (
                    <MenuItem key={currency} value={currency} sx={{ fontSize: 12 }}>{currency}</MenuItem>
                  ))
                }

              </Select>
            </Grid>
          </Grid>

          {/* <Grid container sx={{ mt: 2 }}>
            <Grid item xs={2}><Typography fontSize={18} fontWeight={600}>Payment Frequency</Typography></Grid>
            <Grid item xs={2}>
              <Select
                size='small'
                value={specificPaymentFrequency[id]}
                onChange={(e) => {
                  specificPaymentFrequency[id] = e.target.value;
                  setSpecificPaymentFrequency({ ...specificPaymentFrequency });

                  addToPendingTasks([id]);
                }}
                sx={{ fontSize: 12, height: 40, width: '190px', minWidth: 190 }}
                fullWidth
                disabled={loadingStatus[id]}
              >
                {
                  ["Annually", "Semi Annually", "Quarterly", "Monthly"].map(payment_frequency => (
                    <MenuItem key={payment_frequency} value={payment_frequency}>{payment_frequency}</MenuItem>
                  ))
                }

              </Select>
            </Grid>
          </Grid> */}

          <Grid container sx={{ mt: 1.5 }}>

            <Grid item xs={2}>
              <Typography fontSize={13}>
                Family Discount: <span display='inline-block' style={{ fontSize: 13, color: '#002047', fontWeight: 600 }}>{getFamilyDiscountLabel()}</span>
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography fontSize={13}>
                Area of Coverage: <span display='inline-block' style={{ fontSize: 13, color: '#002047', fontWeight: 600 }}>{getAreaOfCoverage()}</span>
              </Typography>
            </Grid>
          </Grid>

          {
            validationErrorMessages.length > 0 &&
            <ul style={{ color: colors.red['A700'] }}>
              {
                validationErrorMessages.map(validationErrorMessage => {
                  return <li>{validationErrorMessage}</li>
                })
              }
            </ul>
          }

          {
            renderRatesTable({
              // Only numbered once there is a comparison table
              title: comparisons.length > 0 ? 'Table 1' : '',
              tableId: 'table-my-health-philippines-rates',
              getSelection: getMainSelection,
              onSelectionChange: handleMainSelectionChange,
              rates: response?.rates,
              loading: loadingStatus[id]
            })
          }

          {
            !checkIfValidForPremiumSummary() &&
            <Typography style={{ mt: 2, color: colors.orange[400] }}>Not valid for Premium Summary. Clients plans are not the same.</Typography>
          }

          {
            comparisons.map((comparison, comparison_idx) => {
              const getSelection = (clientIdx) => getComparisonSelection(comparison, clientIdx);
              const comparisonErrors = validateMaternityQualification(getSelection);

              return (
                <Box key={comparison.id} sx={{ mt: 1 }}>
                  {
                    comparisonErrors.length > 0 &&
                    <ul style={{ color: colors.red['A700'] }}>
                      {
                        comparisonErrors.map(comparisonError => {
                          return <li key={comparisonError}>{comparisonError}</li>
                        })
                      }
                    </ul>
                  }

                  {
                    renderRatesTable({
                      // Table 1 is the main table
                      title: 'Table ' + (comparison_idx + 2),
                      actions: (
                        <Chip
                          size="small"
                          label="Remove"
                          onClick={() => removeComparison(comparison.id)}
                          sx={{ fontSize: 12, color: '#616161' }}
                        />
                      ),
                      tableId: 'table-my-health-philippines-rates-compare-' + comparison.id,
                      getSelection,
                      onSelectionChange: (clientIdx, field, value) => handleComparisonSelectionChange(comparison.id, clientIdx, field, value),
                      rates: comparisonRates[comparison.id]?.rates,
                      loading: !comparisonRates[comparison.id] || comparisonRates[comparison.id].loading || loadingStatus[id]
                    })
                  }
                </Box>
              );
            })
          }

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 1, mt: 1 }}>
            <Button
              size="small"
              variant="text"
              startIcon={<AddCircleIcon />}
              onClick={addComparison}
              disabled={loadingStatus[id] || clients.length === 0}
              sx={{ fontSize: 12, textTransform: 'none' }}
            >
              Compare
            </Button>
            {
              comparisons.length > 0 &&
              <Button
                size="small"
                variant="text"
                startIcon={<ClearAllIcon />}
                onClick={clearComparisons}
                sx={{ fontSize: 12, textTransform: 'none' }}
              >
                Clear Comparison
              </Button>
            }
          </Box>


          <NotesDisplay appConfig={appConfig} insurerId={id} />

          <RatesVersionSelect
            insurer={id}
            label="Rates Version"
            insurerRates={insurerRates}
            selectedRatesVersion={selectedRatesVersion}
            setSelectedRatesVersion={setSelectedRatesVersion}
            disabled={loadingStatus[id]}
            handleFetchAppConfig={handleFetchAppConfig}
            onChange={() => setShowRefreshNow(true)}
          />


        </Box>


      </Paper>

    </Box>
  );
});

export default MyHealthPhilippines_Rates;